import gsap from 'gsap';
import { AudioEngine, type AudioMetrics } from './audio-engine';
import { AccretionVisualizer, type NebulaPreviewMode, type VisualSettings } from './visualizer';
import './styles.css';

const query = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
};

const engine = new AudioEngine();
const canvas = query<HTMLCanvasElement>('#visualizer-canvas');
const visualField = query<HTMLElement>('#visual-field');
const fallback = query<HTMLElement>('#fallback');
const previewKey = new URLSearchParams(window.location.search).get('preview');
const nebulaMode: NebulaPreviewMode = previewKey === 'nebula-bloom'
  ? 'spectral-bloom'
  : 'spectral-stars';
let visualizer: AccretionVisualizer;

try {
  visualizer = new AccretionVisualizer(canvas, visualField, nebulaMode, true);
} catch (error) {
  fallback.hidden = false;
  throw error;
}

if (previewKey?.startsWith('nebula-')) {
  const previewNav = document.createElement('nav');
  previewNav.className = 'nebula-preview-switcher';
  previewNav.setAttribute('aria-label', 'Nebula background previews');
  previewNav.innerHTML = `
    <span>Nebula colors</span>
    <a href="/" ${nebulaMode === 'spectral-stars' ? 'aria-current="page"' : ''}>Baseline · Atmospheric stars</a>
    <a href="?preview=nebula-bloom" ${nebulaMode === 'spectral-bloom' ? 'aria-current="page"' : ''}>Cloud field only</a>
  `;
  query<HTMLElement>('#instrument').append(previewNav);
}

const trackList = query<HTMLElement>('#track-list');
const playButton = query<HTMLButtonElement>('#play-toggle');
const currentTrack = query<HTMLElement>('#current-track');
const timeReadout = query<HTMLElement>('#time-readout');
const seek = query<HTMLInputElement>('#seek');
const upload = query<HTMLInputElement>('#audio-upload');
const uploadControl = query<HTMLLabelElement>('.upload-control');
const spectrumCanvas = query<HTMLCanvasElement>('#spectrum-canvas');
const orbitalSpectrum = query<HTMLCanvasElement>('#orbital-spectrum');
const spectrumContext = spectrumCanvas.getContext('2d');
const orbitalContext = orbitalSpectrum.getContext('2d');
const bandContainer = query<HTMLElement>('#band-meters');
const systemMessage = query<HTMLElement>('#system-message');
const liveState = query<HTMLElement>('#live-state');
const rangeInputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[type="range"]'));
const bandStacks: HTMLElement[][] = [];
let radialSpectrumLevels = new Float32Array(0);
let lastOrbitalSpectrumFrame = performance.now();
let userSeeking = false;
let messageTimeline: gsap.core.Timeline | null = null;
let previewMotionActive = false;
let mobileExperienceStarted = false;
let lastSheetTrigger: HTMLButtonElement | null = null;

const DEFAULT_BAND_HUES = [190, 198, 207, 220, 270, 315, 348, 16] as const;
const COLOR_THEMES = {
  horizon: DEFAULT_BAND_HUES,
  aurora: [142, 158, 176, 194, 216, 248, 286, 324],
  'deep-ocean': [176, 184, 192, 202, 214, 226, 242, 264],
  'solar-flare': [8, 16, 24, 32, 42, 18, 348, 326],
  ultraviolet: [244, 258, 274, 290, 306, 322, 338, 354],
  spectrum: [4, 48, 92, 138, 184, 228, 274, 320],
} as const;
type ColorThemeId = keyof typeof COLOR_THEMES;

const CURATED_TRACKS = [
  { id: 'chill', title: 'Chill', artist: 'Paul Yudin', src: '/audio/paul-yudin-chill.mp3' },
  { id: 'snow-glow', title: 'Snow Glow', artist: 'Syouki Takahashi', src: '/audio/syouki-takahashi-snow-glow.mp3' },
  { id: 'another-galaxy', title: 'Another Galaxy', artist: 'Syouki Takahashi', src: '/audio/syouki-takahashi-another-galaxy.mp3' },
] as const;
const STARTUP_TRACK = CURATED_TRACKS[1];
const MOBILE_STARTUP_TRACK = CURATED_TRACKS[0];
let curatedLoadingId: string | null = null;

const visualSettings: VisualSettings = {
  intensity: 0.94,
  distortion: 0.38,
  travel: 0.62,
  orbSize: 1,
  orbMorph: 0.38,
  orbResponse: 1,
  orbTurbulence: 0.52,
  orbBassPulse: 1,
  orbCohesion: 0.55,
  orbTopology: 0,
  orbFragmentation: 0,
  bandHues: DEFAULT_BAND_HUES.map((hue) => hue / 360),
};

const colorThemeSelect = query<HTMLSelectElement>('#particle-color-theme');
const colorThemePreview = query<HTMLElement>('#color-theme-preview');

const updateColorThemePreview = (): void => {
  const stops = visualSettings.bandHues
    .map((hue, index) => `hsl(${Math.round(hue * 360)} 88% 62%) ${index / 7 * 100}%`)
    .join(', ');
  colorThemePreview.style.background = `linear-gradient(90deg, ${stops})`;
};

const markColorThemeCustom = (): void => {
  colorThemeSelect.value = 'custom';
};

const formatTime = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60).toString().padStart(2, '0');
  const remainder = (safe % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
};

const decibels = (value: number): string => value > 0.00001 ? `${(20 * Math.log10(value)).toFixed(1)} dB` : '−∞ dB';

const setRangeProgress = (input: HTMLInputElement): void => {
  const min = Number(input.min || 0);
  const max = Number(input.max || 100);
  const ratio = (Number(input.value) - min) / Math.max(1, max - min);
  input.style.setProperty('--range-progress', `${Math.max(0, Math.min(1, ratio)) * 100}%`);
};

const getPercentEditor = (range: HTMLInputElement): HTMLInputElement | null =>
  document.querySelector<HTMLInputElement>(`.slider-value-input[data-for="${range.id}"]`);

const syncPercentEditor = (range: HTMLInputElement): void => {
  const editor = getPercentEditor(range);
  if (editor && editor.value !== range.value) editor.value = range.value;
};

const setPercentControlDisabled = (range: HTMLInputElement, disabled: boolean): void => {
  range.disabled = disabled;
  const editor = getPercentEditor(range);
  if (editor) editor.disabled = disabled;
};

const showMessage = (message: string, error = false, prominent = false): void => {
  systemMessage.textContent = message;
  systemMessage.classList.toggle('is-error', error);
  systemMessage.classList.toggle('is-prominent', prominent);
  messageTimeline?.kill();
  messageTimeline = gsap.timeline()
    .to(systemMessage, { opacity: 1, y: 0, duration: 0.32, ease: 'power3.out' })
    .to(systemMessage, { opacity: 0, y: -8, duration: 0.35, ease: 'power2.in', delay: error ? 4.5 : prominent ? 3 : 2.4 });
};

const isAutoplayBlocked = (error: unknown): boolean => {
  const name = error instanceof Error ? error.name : '';
  return name === 'NotAllowedError' || name === 'AbortError' || name === 'NotSupportedError';
};

const setPreviewMotion = (active: boolean): void => {
  previewMotionActive = active;
  engine.setPreviewing(active);
  visualizer.setPreviewing(active);
  if (active) { liveState.textContent = 'PREVIEW'; liveState.style.color = 'var(--cyan)'; }
};

const tryStartPlayback = async (announce = true): Promise<boolean> => {
  try {
    await engine.play();
    setPreviewMotion(false);
    return true;
  } catch (error) {
    if (!isAutoplayBlocked(error)) throw error;
    setPreviewMotion(true);
    if (announce) showMessage('Visual preview active. Tap Play to enable audio.', false, true);
    return false;
  }
};

const loadCuratedTrack = async (track: typeof CURATED_TRACKS[number], announce = true): Promise<boolean> => {
  if (curatedLoadingId) return false;
  if (engine.track.id !== track.id) {
    curatedLoadingId = track.id;
    renderTrackState();
    playButton.disabled = true;
    try {
      await engine.loadTrack(track.src, track.title, track.id);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'This track could not be loaded. Try again.', true);
      return false;
    } finally {
      curatedLoadingId = null;
      renderTrackState();
      updateTrackUI();
    }
  }
  const started = await tryStartPlayback(announce);
  if (started && announce) showMessage(track.title + ' by ' + track.artist + ' is now in orbit.');
  return started;
};
const renderTrackState = (): void => {
  trackList.replaceChildren();
  CURATED_TRACKS.forEach((track) => {
    const button = document.createElement('button');
    const isActive = engine.track.id === track.id;
    const isLoading = curatedLoadingId === track.id;
    button.type = 'button';
    button.className = 'track-option';
    button.classList.toggle('is-active', isActive);
    button.classList.toggle('is-loading', isLoading);
    button.disabled = curatedLoadingId !== null;
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute('aria-label', `${isLoading ? 'Loading' : 'Select'} ${track.title} by ${track.artist}`);

    const marker = document.createElement('span');
    marker.className = 'track-option__marker';
    marker.setAttribute('aria-hidden', 'true');
    const copy = document.createElement('span');
    copy.className = 'track-option__copy';
    const title = document.createElement('strong');
    title.textContent = track.title;
    const detail = document.createElement('small');
    detail.textContent = isLoading ? 'Loading audio…' : track.artist;
    copy.append(title, detail);
    button.append(marker, copy);
    button.addEventListener('click', () => void handleCuratedTrack(track));
    trackList.append(button);
  });
};

const buildBandMeters = (): void => {
  const labels = [
    ['20–60', '20'], ['60–150', '60'], ['150–400', '150'], ['400–1K', '400'],
    ['1K–2.5K', '1K'], ['2.5K–6K', '2.5K'], ['6K–15K', '6K'], ['15K–20K', '20K'],
  ] as const;
  labels.forEach(([label, shortLabel]) => {
    const meter = document.createElement('div');
    meter.className = 'band-meter';
    const name = document.createElement('span');
    name.textContent = shortLabel;
    name.dataset.short = shortLabel;
    meter.setAttribute('aria-label', `${label} hertz range`);
    const stack = document.createElement('div');
    stack.className = 'band-stack';
    const segments: HTMLElement[] = [];
    for (let index = 0; index < 14; index += 1) {
      const segment = document.createElement('i');
      stack.append(segment);
      segments.push(segment);
    }
    const output = document.createElement('output');
    output.textContent = '−∞';
    meter.append(name, stack, output);
    bandContainer.append(meter);
    bandStacks.push(segments);
  });
};

const updateTrackUI = (): void => {
  const track = engine.track;
  currentTrack.textContent = track.title;
  playButton.disabled = !engine.hasTrack;
  seek.disabled = !engine.hasTrack;
  query<HTMLButtonElement>('#mute-toggle').disabled = !engine.hasTrack;
  playButton.setAttribute('aria-label', engine.hasTrack
    ? `${engine.state === 'playing' ? 'Pause' : 'Play'} ${track.title}`
    : 'Choose a track or upload audio before playback');
  timeReadout.textContent = `${formatTime(engine.position)} / ${formatTime(track.duration)}`;
  query<HTMLElement>('#format-readout').textContent = track.uploaded ? 'UPLOADED' : engine.hasTrack ? 'MP3' : '—';
};

const handleCuratedTrack = async (track: typeof CURATED_TRACKS[number]): Promise<void> => {
  if (curatedLoadingId || (engine.track.id === track.id && engine.state === 'playing')) return;
  try {
    await loadCuratedTrack(track);
  } catch (error) {
    showMessage(error instanceof Error ? error.message : 'This track could not be started. Try again.', true);
  }
};
const handleFile = async (file: File | undefined): Promise<void> => {
  if (!file) return;
  if (!file.type.startsWith('audio/') && !/\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(file.name)) {
    showMessage('Choose an audio file such as MP3, WAV, M4A, AAC, OGG, or FLAC.', true);
    return;
  }
  try {
    curatedLoadingId = null;
    await engine.loadFile(file);
    const started = await tryStartPlayback();
    if (started) showMessage(`${engine.track.title} is now in orbit.`);
  } catch (error) {
    showMessage(error instanceof Error ? error.message : 'This audio file could not be decoded. Try another format.', true);
  } finally {
    upload.value = '';
  }
};

const drawSpectrum = (context: CanvasRenderingContext2D, canvasElement: HTMLCanvasElement, data: Uint8Array<ArrayBuffer>): void => {
  const width = canvasElement.width;
  const height = canvasElement.height;
  context.clearRect(0, 0, width, height);
  const gradient = context.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0, '#00cffa');
  gradient.addColorStop(0.52, '#18bce4');
  gradient.addColorStop(0.72, '#e58b6f');
  gradient.addColorStop(1, '#ff4a16');
  const bins = 72;
  const gap = 4;
  const barWidth = Math.max(1, (width - gap * (bins - 1)) / bins);
  context.fillStyle = gradient;
  context.globalAlpha = 0.9;
  for (let index = 0; index < bins; index += 1) {
    const normalized = index / (bins - 1);
    const logIndex = Math.floor((Math.pow(18, normalized) - 1) / 17 * (data.length - 1));
    const value = data[logIndex] / 255;
    const barHeight = Math.max(1, value * height * 0.9);
    const x = index * (barWidth + gap);
    context.fillRect(x, height - barHeight, barWidth, barHeight);
  }
  context.globalAlpha = 0.18;
  context.strokeStyle = '#4a7180';
  context.lineWidth = 1;
  const lines = 6;
  for (let index = 1; index < lines; index += 1) {
    const y = Math.round(index / lines * height) + 0.5;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
  context.globalAlpha = 1;
};

const drawOrbitalSpectrum = (metrics: AudioMetrics): void => {
  const now = performance.now();
  const frameDelta = Math.min(0.05, Math.max(0.001, (now - lastOrbitalSpectrumFrame) / 1000));
  lastOrbitalSpectrumFrame = now;
  const width = visualField.clientWidth;
  const height = visualField.clientHeight;
  const dpr = Math.min(window.devicePixelRatio, 1.5);
  if (orbitalSpectrum.width !== Math.round(width * dpr) || orbitalSpectrum.height !== Math.round(height * dpr)) {
    orbitalSpectrum.width = Math.round(width * dpr);
    orbitalSpectrum.height = Math.round(height * dpr);
  }
  const context = orbitalContext!;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);
  const desktop = width > 900;
  const leftRail = desktop ? Math.min(366, Math.max(300, width * 0.219)) : 0;
  const rightRail = desktop ? Math.min(304, Math.max(270, width * 0.182)) : 0;
  const centerX = width / 2 + (leftRail - rightRail) / 2;
  // The Three.js camera keeps the disk group centered in the visual field on
  // mobile. Keep the overlay ring on that same optical center instead of the
  // older 42% anchor that was tuned around the transport clearance.
  const centerY = desktop ? height * 0.493 : height * 0.5;
  let radius = Math.min(width * 0.282, height * 0.385);
  if (!desktop) {
    // Match the ring to the mobile camera's world-to-pixel scale. A
    // height-only radius works in portrait but becomes a flat, undersized
    // loop in landscape; deriving it from the same perspective framing
    // keeps the ring around the disk and center orb at every aspect ratio.
    const aspect = width / Math.max(1, height);
    const cameraZ = aspect < 0.76 ? 17.7 : aspect < 1.15 ? 12.4 : 8.8;
    const fovRadians = 48 * Math.PI / 180;
    const pixelsPerWorldUnit = height / (2 * cameraZ * Math.tan(fovRadians / 2));
    const sceneRadius = 3.4;
    const projectedRadius = pixelsPerWorldUnit * sceneRadius;
    const viewportRadius = Math.min(width, height) * 0.47;
    radius = Math.min(projectedRadius, viewportRadius);
  }
  const pointCount = desktop ? 104 : 64;
  if (radialSpectrumLevels.length !== pointCount) {
    radialSpectrumLevels = new Float32Array(pointCount);
  }

  for (let index = 0; index < pointCount; index += 1) {
    const progress = index / pointCount;
    const angle = progress * Math.PI * 2 - Math.PI / 2;
    const bandIndex = Math.min(7, Math.floor(progress * 8));
    const logIndex = Math.floor((Math.pow(18, progress) - 1) / 17 * (metrics.frequency.length - 1));
    let targetEnergy = 0;
    let sampleCount = 0;
    for (let offset = -1; offset <= 1; offset += 1) {
      const sampleIndex = Math.max(0, Math.min(metrics.frequency.length - 1, logIndex + offset));
      targetEnergy += metrics.frequency[sampleIndex] / 255;
      sampleCount += 1;
    }
    targetEnergy /= sampleCount;
    const currentEnergy = radialSpectrumLevels[index];
    const responseRate = targetEnergy > currentEnergy ? 12 : 5.2;
    const energy = currentEnergy + (targetEnergy - currentEnergy) * (1 - Math.exp(-frameDelta * responseRate));
    radialSpectrumLevels[index] = energy;
    const outward = energy * (desktop ? 30 : 16);
    context.fillStyle = `hsl(${Math.round(visualSettings.bandHues[bandIndex] * 360)} 88% 64%)`;
    context.globalAlpha = 0.25 + energy * 0.7;
    const innerRadius = radius - (desktop ? 2 : 1);
    const barLength = (desktop ? 3.5 : 2.5) + outward;
    context.strokeStyle = context.fillStyle;
    context.lineWidth = desktop ? 1.35 : 1.1;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(
      centerX + Math.cos(angle) * innerRadius,
      centerY + Math.sin(angle) * innerRadius,
    );
    context.lineTo(
      centerX + Math.cos(angle) * (innerRadius + barLength),
      centerY + Math.sin(angle) * (innerRadius + barLength),
    );
    context.stroke();
  }
  context.globalAlpha = 1;
};

const updateMeters = (metrics: AudioMetrics): void => {
  metrics.bands.forEach((value, meterIndex) => {
    const lit = Math.round(value * bandStacks[meterIndex].length);
    bandStacks[meterIndex].forEach((segment, segmentIndex) => {
      segment.classList.toggle('is-lit', segmentIndex < lit);
      segment.classList.toggle('is-hot', segmentIndex < lit && segmentIndex >= bandStacks[meterIndex].length - 3);
    });
    const output = bandContainer.children[meterIndex]?.querySelector('output');
    if (output) output.textContent = value > 0.001 ? (20 * Math.log10(value)).toFixed(1) : '−∞';
  });
};

const updateTelemetry = (metrics: AudioMetrics): void => {
  const peakDb = decibels(metrics.peak);
  query<HTMLElement>('#peak-db').textContent = peakDb;
  query<HTMLElement>('#rms-readout').textContent = decibels(metrics.rms);
  query<HTMLElement>('#signal-peak').textContent = peakDb;
  query<HTMLElement>('#centroid-readout').textContent = metrics.centroid > 0 ? `${(metrics.centroid / 1000).toFixed(2)} kHz` : '—';
  query<HTMLElement>('#sample-rate').textContent = engine.sampleRate ? `${engine.sampleRate} Hz` : '—';
  const scope = query<HTMLElement>('.stereo-scope i');
  scope.style.transform = `translate(calc(-50% + ${(metrics.mid - metrics.low) * 25}px), calc(-50% + ${(metrics.high - metrics.mid) * 20}px)) scale(${0.7 + metrics.rms * 1.8})`;
};

const frame = (): void => {
  const metrics = engine.getMetrics();
  applyAutoAdjust();
  applyOrbAutoAdjust();
  applyColorAutoAdjust();
  visualizer.setMetrics(metrics);
  drawSpectrum(spectrumContext!, spectrumCanvas, metrics.frequency);
  drawOrbitalSpectrum(metrics);
  updateMeters(metrics);
  updateTelemetry(metrics);

  if (!userSeeking) {
    const ratio = engine.hasTrack && engine.track.duration ? engine.position / engine.track.duration : 0;
    seek.value = Math.round(ratio * 1000).toString();
    setRangeProgress(seek);
  }
  timeReadout.textContent = `${formatTime(engine.position)} / ${formatTime(engine.track.duration)}`;
  requestAnimationFrame(frame);
};

const syncSheetSemantics = (): void => {
  const mobile = window.matchMedia('(max-width: 900px)').matches;
  document.querySelectorAll<HTMLElement>('.hud-rail').forEach((panel) => {
    if (!mobile) {
      panel.inert = false;
      panel.removeAttribute('aria-hidden');
      return;
    }
    const open = panel.classList.contains('is-open');
    panel.inert = !open;
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  });
  document.querySelectorAll<HTMLButtonElement>('[data-open-sheet]').forEach((button) => {
    const panel = query<HTMLElement>(`#${button.dataset.openSheet}`);
    button.setAttribute('aria-expanded', mobile && panel.classList.contains('is-open') ? 'true' : 'false');
  });
};

const openSheet = (id: string, trigger?: HTMLButtonElement): void => {
  lastSheetTrigger = trigger ?? null;
  document.querySelectorAll('.hud-rail.is-open').forEach((panel) => panel.classList.remove('is-open'));
  const panel = query<HTMLElement>(`#${id}`);
  panel.classList.add('is-open');
  query<HTMLElement>('#sheet-scrim').classList.add('is-open');
  syncSheetSemantics();
  requestAnimationFrame(() => panel.querySelector<HTMLButtonElement>('[data-close-sheet]')?.focus());
};

const closeSheets = (): void => {
  document.querySelectorAll('.hud-rail.is-open').forEach((panel) => panel.classList.remove('is-open'));
  query<HTMLElement>('#sheet-scrim').classList.remove('is-open');
  syncSheetSemantics();
  lastSheetTrigger?.focus();
  lastSheetTrigger = null;
};

renderTrackState();
buildBandMeters();
rangeInputs.forEach((input) => {
  setRangeProgress(input);
  input.addEventListener('input', () => setRangeProgress(input));
});

playButton.addEventListener('click', async () => {
  try {
    if (engine.state === 'playing') {
      await engine.toggle();
    } else {
      await tryStartPlayback();
    }
  } catch (error) {
    showMessage(error instanceof Error ? error.message : 'Audio could not start. Try again.', true);
  }
});

visualField.addEventListener('pointerenter', (event) => {
  if (event.pointerType !== 'mouse' || engine.state === 'playing' || curatedLoadingId) return;
  void loadCuratedTrack(STARTUP_TRACK, false).then((started) => {
    if (!started && previewMotionActive) showMessage('Visual preview active. Tap Play to enable audio.', false, true);
  });
});

visualField.addEventListener('pointerdown', () => {
  if (engine.state === 'playing' || curatedLoadingId) return;
  void loadCuratedTrack(STARTUP_TRACK, false).then((started) => {
    if (!started && previewMotionActive) showMessage('Visual preview active. Tap Play to enable audio.', false, true);
  });
});
query<HTMLButtonElement>('#mute-toggle').addEventListener('click', () => {
  const muted = engine.toggleMute();
  query<HTMLButtonElement>('#mute-toggle').setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  showMessage(muted ? 'Audio muted' : 'Audio restored');
});

seek.addEventListener('pointerdown', () => { userSeeking = true; });
seek.addEventListener('input', () => {
  const position = Number(seek.value) / 1000 * engine.track.duration;
  timeReadout.textContent = `${formatTime(position)} / ${formatTime(engine.track.duration)}`;
});
seek.addEventListener('change', () => {
  userSeeking = false;
  void engine.seek(Number(seek.value) / 1000 * engine.track.duration);
});
seek.addEventListener('pointerup', () => { userSeeking = false; });

upload.addEventListener('change', () => void handleFile(upload.files?.[0]));
for (const eventName of ['dragenter', 'dragover']) {
  uploadControl.addEventListener(eventName, (event) => {
    event.preventDefault();
    uploadControl.classList.add('is-dragging');
  });
}
for (const eventName of ['dragleave', 'drop']) {
  uploadControl.addEventListener(eventName, (event) => {
    event.preventDefault();
    uploadControl.classList.remove('is-dragging');
  });
}
uploadControl.addEventListener('drop', (event) => void handleFile(event.dataTransfer?.files[0]));

type ScalarVisualSetting = Exclude<keyof VisualSettings, 'bandHues'>;
type AutoAdjustKey = 'intensity' | 'distortion' | 'travel';

const autoAdjustToggle = query<HTMLButtonElement>('#auto-adjust-toggle');
const autoAdjustOptions = query<HTMLElement>('#auto-adjust-options');
const autoAdjustState = query<HTMLElement>('#auto-adjust-state');
const autoAdjustChecks = Array.from(autoAdjustOptions.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
const autoAdjustActive = new Set<AutoAdjustKey>();
type AutoAdjustMotion = {
  from: number;
  target: number;
  startedAt: number;
  duration: number;
  segment: number;
  nextEdge: 'min' | 'max';
};
const autoAdjustMotion = new Map<AutoAdjustKey, AutoAdjustMotion>();
const AUTO_ADJUST_BOUNDS: Record<AutoAdjustKey, readonly [number, number]> = {
  intensity: [0.2, 1.1],
  distortion: [0, 1],
  travel: [0, 1],
};

const randomDuration = (): number => 3600 + Math.random() * 3400;

const pickAutoTarget = (bounds: readonly [number, number], motion: AutoAdjustMotion): number => {
  const [minimum, maximum] = bounds;
  if (motion.segment > 0 && motion.segment % 3 === 0) {
    const edge = motion.nextEdge === 'min' ? minimum : maximum;
    motion.nextEdge = motion.nextEdge === 'min' ? 'max' : 'min';
    return edge;
  }
  return minimum + Math.random() * (maximum - minimum);
};

const createAutoMotion = (value: number, bounds: readonly [number, number]): AutoAdjustMotion => {
  const motion: AutoAdjustMotion = {
    from: value,
    target: value,
    startedAt: performance.now(),
    duration: randomDuration(),
    segment: 0,
    nextEdge: Math.random() < 0.5 ? 'min' : 'max',
  };
  motion.target = pickAutoTarget(bounds, motion);
  return motion;
};

const advanceAutoMotion = (motion: AutoAdjustMotion, bounds: readonly [number, number], now: number): number => {
  const progress = Math.min(1, (now - motion.startedAt) / motion.duration);
  const eased = progress * progress * progress * (progress * (progress * 6 - 15) + 10);
  const value = motion.from + (motion.target - motion.from) * eased;
  if (progress >= 1) {
    motion.from = motion.target;
    motion.segment += 1;
    motion.target = pickAutoTarget(bounds, motion);
    motion.startedAt = now;
    motion.duration = randomDuration();
  }
  return value;
};

const setEffectRange = (key: AutoAdjustKey, value: number): void => {
  const input = query<HTMLInputElement>(`#${key}`);
  const percent = Math.round(value * 100);
  if (input.value !== percent.toString()) input.value = percent.toString();
  syncPercentEditor(input);
  setRangeProgress(input);
  visualSettings[key] = value;
};

const syncAutoAdjustUI = (): void => {
  const count = autoAdjustActive.size;
  autoAdjustState.textContent = count === 0 ? 'Off' : `${count} flowing`;
  autoAdjustToggle.classList.toggle('is-active', count > 0);
  autoAdjustToggle.setAttribute('aria-label', count === 0 ? 'Configure Auto-Adjust' : `Configure Auto-Adjust, ${count} flowing`);
};

const applyAutoAdjust = (): void => {
  if (autoAdjustActive.size === 0) return;
  const now = performance.now();
  const updates: Partial<VisualSettings> = {};

  for (const key of autoAdjustActive) {
    let motion = autoAdjustMotion.get(key);
    if (!motion) {
      motion = createAutoMotion(visualSettings[key], AUTO_ADJUST_BOUNDS[key]);
      autoAdjustMotion.set(key, motion);
    }

    const value = advanceAutoMotion(motion, AUTO_ADJUST_BOUNDS[key], now);
    setEffectRange(key, value);
    updates[key] = value;
  }

  visualizer.setSettings(updates);
};

autoAdjustToggle.addEventListener('click', () => toggleAutoAdjustPanel(autoAdjustToggle, autoAdjustOptions));

autoAdjustChecks.forEach((checkbox) => {
  checkbox.addEventListener('change', () => {
    const key = checkbox.value as AutoAdjustKey;
    const input = query<HTMLInputElement>(`#${key}`);
    const row = input.closest<HTMLElement>('.slider-row');
    if (checkbox.checked) {
      autoAdjustActive.add(key);
      autoAdjustMotion.set(key, createAutoMotion(visualSettings[key], AUTO_ADJUST_BOUNDS[key]));
      setPercentControlDisabled(input, true);
      row?.classList.add('is-auto');
    } else {
      autoAdjustActive.delete(key);
      autoAdjustMotion.delete(key);
      setPercentControlDisabled(input, false);
      row?.classList.remove('is-auto');
    }
    syncAutoAdjustUI();
  });
});

const clearAutoAdjust = (): void => {
  autoAdjustChecks.forEach((checkbox) => {
    checkbox.checked = false;
    const key = checkbox.value as AutoAdjustKey;
    const input = query<HTMLInputElement>(`#${key}`);
    setPercentControlDisabled(input, false);
    input.closest<HTMLElement>('.slider-row')?.classList.remove('is-auto');
  });
  autoAdjustActive.clear();
  autoAdjustMotion.clear();
  autoAdjustOptions.hidden = true;
  autoAdjustToggle.setAttribute('aria-expanded', 'false');
  syncAutoAdjustUI();
};

type OrbAutoAdjustKey = 'orbResponse' | 'orbSize' | 'orbMorph' | 'orbBassPulse' | 'orbTopology' | 'orbFragmentation';
const ORB_AUTO_CONTROLS: Record<OrbAutoAdjustKey, { inputId: string; bounds: readonly [number, number] }> = {
  orbResponse: { inputId: 'orb-response', bounds: [0, 1.8] },
  orbSize: { inputId: 'orb-size', bounds: [0.6, 1.4] },
  orbMorph: { inputId: 'orb-morph', bounds: [0, 1] },
  orbBassPulse: { inputId: 'orb-bass-pulse', bounds: [0, 1.8] },
  orbTopology: { inputId: 'orb-topology', bounds: [0, 1] },
  orbFragmentation: { inputId: 'orb-fragmentation', bounds: [0, 1] },
};
const orbAutoAdjustToggle = query<HTMLButtonElement>('#orb-auto-adjust-toggle');
const orbAutoAdjustOptions = query<HTMLElement>('#orb-auto-adjust-options');
const orbAutoAdjustState = query<HTMLElement>('#orb-auto-adjust-state');
const orbAutoAdjustChecks = Array.from(orbAutoAdjustOptions.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
const orbAutoAdjustActive = new Set<OrbAutoAdjustKey>();
const orbAutoAdjustMotion = new Map<OrbAutoAdjustKey, AutoAdjustMotion>();

const setOrbRange = (key: OrbAutoAdjustKey, value: number): void => {
  const input = query<HTMLInputElement>(`#${ORB_AUTO_CONTROLS[key].inputId}`);
  const percent = Math.round(value * 100);
  if (input.value !== percent.toString()) input.value = percent.toString();
  syncPercentEditor(input);
  setRangeProgress(input);
  visualSettings[key] = value;
};

const syncOrbAutoAdjustUI = (): void => {
  const count = orbAutoAdjustActive.size;
  orbAutoAdjustState.textContent = count === 0 ? 'Off' : `${count} flowing`;
  orbAutoAdjustToggle.classList.toggle('is-active', count > 0);
  orbAutoAdjustToggle.setAttribute('aria-label', count === 0 ? 'Configure Orb Auto-Adjust' : `Configure Orb Auto-Adjust, ${count} flowing`);
};

const applyOrbAutoAdjust = (): void => {
  if (orbAutoAdjustActive.size === 0) return;
  const now = performance.now();
  const updates: Partial<VisualSettings> = {};
  for (const key of orbAutoAdjustActive) {
    const control = ORB_AUTO_CONTROLS[key];
    let motion = orbAutoAdjustMotion.get(key);
    if (!motion) {
      motion = createAutoMotion(visualSettings[key], control.bounds);
      orbAutoAdjustMotion.set(key, motion);
    }
    const value = advanceAutoMotion(motion, control.bounds, now);
    setOrbRange(key, value);
    updates[key] = value;
  }
  visualizer.setSettings(updates);
};

orbAutoAdjustToggle.addEventListener('click', () => toggleAutoAdjustPanel(orbAutoAdjustToggle, orbAutoAdjustOptions));

orbAutoAdjustChecks.forEach((checkbox) => {
  checkbox.addEventListener('change', () => {
    const key = checkbox.value as OrbAutoAdjustKey;
    const control = ORB_AUTO_CONTROLS[key];
    const input = query<HTMLInputElement>(`#${control.inputId}`);
    const row = input.closest<HTMLElement>('.slider-row');
    if (checkbox.checked) {
      orbAutoAdjustActive.add(key);
      orbAutoAdjustMotion.set(key, createAutoMotion(visualSettings[key], control.bounds));
      setPercentControlDisabled(input, true);
      row?.classList.add('is-auto');
    } else {
      orbAutoAdjustActive.delete(key);
      orbAutoAdjustMotion.delete(key);
      setPercentControlDisabled(input, false);
      row?.classList.remove('is-auto');
    }
    syncOrbAutoAdjustUI();
  });
});

const clearOrbAutoAdjust = (): void => {
  orbAutoAdjustChecks.forEach((checkbox) => {
    checkbox.checked = false;
    const key = checkbox.value as OrbAutoAdjustKey;
    const input = query<HTMLInputElement>(`#${ORB_AUTO_CONTROLS[key].inputId}`);
    setPercentControlDisabled(input, false);
    input.closest<HTMLElement>('.slider-row')?.classList.remove('is-auto');
  });
  orbAutoAdjustActive.clear();
  orbAutoAdjustMotion.clear();
  orbAutoAdjustOptions.hidden = true;
  orbAutoAdjustToggle.setAttribute('aria-expanded', 'false');
  syncOrbAutoAdjustUI();
};

const gyrationToggle = query<HTMLButtonElement>('#gyration-toggle');
const gyrationState = query<HTMLElement>('.gyration-state');

const setGyrationEnabled = (enabled: boolean): void => {
  gyrationToggle.setAttribute('aria-checked', enabled.toString());
  gyrationToggle.classList.toggle('is-active', enabled);
  gyrationState.textContent = enabled ? 'On' : 'Off';
  visualizer.setGyration(enabled);
};

gyrationToggle.addEventListener('click', () => {
  const enabled = gyrationToggle.getAttribute('aria-checked') !== 'true';
  setGyrationEnabled(enabled);
  showMessage(enabled ? 'Auto Orbit engaged' : 'Manual 3D control restored');
});

const colorAutoAdjustToggle = query<HTMLButtonElement>('#color-auto-adjust-toggle');
const colorAutoAdjustOptions = query<HTMLElement>('#color-auto-adjust-options');
const colorAutoAdjustState = query<HTMLElement>('#color-auto-adjust-state');
const colorAutoAdjustChecks = Array.from(colorAutoAdjustOptions.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
const colorAutoAdjustActive = new Set<number>();
const colorAutoAdjustMotion = new Map<number, AutoAdjustMotion>();
const COLOR_AUTO_BOUNDS = [0, 360] as const;

const syncColorAutoAdjustUI = (): void => {
  const count = colorAutoAdjustActive.size;
  colorAutoAdjustState.textContent = count === 0 ? 'Off' : `${count} flowing`;
  colorAutoAdjustToggle.classList.toggle('is-active', count > 0);
  colorAutoAdjustToggle.setAttribute('aria-label', count === 0 ? 'Configure Color Auto-Adjust' : `Configure Color Auto-Adjust, ${count} flowing`);
};

const applyColorAutoAdjust = (): void => {
  if (colorAutoAdjustActive.size === 0) return;
  const now = performance.now();
  for (const bandIndex of colorAutoAdjustActive) {
    let motion = colorAutoAdjustMotion.get(bandIndex);
    if (!motion) {
      motion = createAutoMotion(visualSettings.bandHues[bandIndex] * 360, COLOR_AUTO_BOUNDS);
      colorAutoAdjustMotion.set(bandIndex, motion);
    }
    const degrees = advanceAutoMotion(motion, COLOR_AUTO_BOUNDS, now);
    setBandHueValue(query<HTMLInputElement>(`#band-hue-${bandIndex}`), bandIndex, degrees, false);
  }
  visualizer.setSettings({ bandHues: [...visualSettings.bandHues] });
  updateColorThemePreview();
};

colorAutoAdjustToggle.addEventListener('click', () => toggleAutoAdjustPanel(colorAutoAdjustToggle, colorAutoAdjustOptions));

colorAutoAdjustChecks.forEach((checkbox) => {
  checkbox.addEventListener('change', () => {
    const bandIndex = Number(checkbox.value);
    const input = query<HTMLInputElement>(`#band-hue-${bandIndex}`);
    const row = input.closest<HTMLElement>('.slider-row');
    const currentDegrees = visualSettings.bandHues[bandIndex] * 360;
    if (checkbox.checked) {
      markColorThemeCustom();
      colorAutoAdjustActive.add(bandIndex);
      colorAutoAdjustMotion.set(bandIndex, createAutoMotion(currentDegrees, COLOR_AUTO_BOUNDS));
      input.disabled = true;
      row?.classList.add('is-auto');
    } else {
      colorAutoAdjustActive.delete(bandIndex);
      colorAutoAdjustMotion.delete(bandIndex);
      input.disabled = false;
      row?.classList.remove('is-auto');
    }
    updateColorThemePreview();
    syncColorAutoAdjustUI();
  });
});

const clearColorAutoAdjust = (): void => {
  colorAutoAdjustChecks.forEach((checkbox) => {
    checkbox.checked = false;
    const bandIndex = Number(checkbox.value);
    const input = query<HTMLInputElement>(`#band-hue-${bandIndex}`);
    input.disabled = false;
    input.closest<HTMLElement>('.slider-row')?.classList.remove('is-auto');
  });
  colorAutoAdjustActive.clear();
  colorAutoAdjustMotion.clear();
  colorAutoAdjustOptions.hidden = true;
  colorAutoAdjustToggle.setAttribute('aria-expanded', 'false');
  syncColorAutoAdjustUI();
};

const bindAutoAdjustBulk = (container: HTMLElement, checkboxes: HTMLInputElement[]): void => {
  container.querySelectorAll<HTMLButtonElement>('[data-auto-bulk]').forEach((button) => {
    button.addEventListener('click', () => {
      const checked = button.dataset.autoBulk === 'select';
      checkboxes.forEach((checkbox) => {
        if (checkbox.checked === checked) return;
        checkbox.checked = checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
};

function toggleAutoAdjustPanel(toggle: HTMLButtonElement, options: HTMLElement): void {
  const opening = toggle.getAttribute('aria-expanded') !== 'true';
  document.querySelectorAll<HTMLButtonElement>('.auto-adjust-toggle').forEach((otherToggle) => {
    const panelId = otherToggle.getAttribute('aria-controls');
    if (!panelId) return;
    const panel = document.getElementById(panelId);
    otherToggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  });
  if (opening) {
    toggle.setAttribute('aria-expanded', 'true');
    options.hidden = false;
  }
}

bindAutoAdjustBulk(autoAdjustOptions, autoAdjustChecks);
bindAutoAdjustBulk(orbAutoAdjustOptions, orbAutoAdjustChecks);
bindAutoAdjustBulk(colorAutoAdjustOptions, colorAutoAdjustChecks);

const enableAutoAdjustSelections = (checks: HTMLInputElement[]): void => {
  checks.forEach((checkbox) => {
    if (checkbox.checked) return;
    checkbox.checked = true;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  });
};

const enableMobileStartupAutoAdjust = (): void => {
  if (!window.matchMedia('(max-width: 900px)').matches) return;
  enableAutoAdjustSelections(autoAdjustChecks);
  enableAutoAdjustSelections(orbAutoAdjustChecks);
  enableAutoAdjustSelections(colorAutoAdjustChecks);
};

const bindVisualRange = (id: string, key: ScalarVisualSetting, divisor: number): void => {
  const range = query<HTMLInputElement>(`#${id}`);
  const editor = getPercentEditor(range);
  range.addEventListener('input', (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const normalized = Number(input.value) / divisor;
    visualSettings[key] = normalized;
    visualizer.setSettings({ [key]: normalized });
    syncPercentEditor(input);
  });

  if (!editor) return;

  const commitEditor = (): void => {
    const parsed = Number(editor.value);
    if (editor.value.trim() === '' || !Number.isFinite(parsed)) {
      editor.value = range.value;
      return;
    }
    const minimum = Number(range.min);
    const maximum = Number(range.max);
    const clamped = Math.min(maximum, Math.max(minimum, Math.round(parsed)));
    editor.value = clamped.toString();
    range.value = clamped.toString();
    range.dispatchEvent(new Event('input', { bubbles: true }));
    setRangeProgress(range);
  };

  editor.addEventListener('input', () => {
    if (editor.value.trim() === '') return;
    const parsed = Number(editor.value);
    if (!Number.isFinite(parsed) || parsed < Number(range.min) || parsed > Number(range.max)) return;
    range.value = Math.round(parsed).toString();
    range.dispatchEvent(new Event('input', { bubbles: true }));
    setRangeProgress(range);
  });
  editor.addEventListener('change', commitEditor);
  editor.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      commitEditor();
      editor.blur();
    }
    if (event.key === 'Escape') {
      editor.value = range.value;
      editor.blur();
    }
  });
};

bindVisualRange('intensity', 'intensity', 100);
bindVisualRange('distortion', 'distortion', 100);
bindVisualRange('travel', 'travel', 100);
bindVisualRange('orb-size', 'orbSize', 100);
bindVisualRange('orb-morph', 'orbMorph', 100);
bindVisualRange('orb-response', 'orbResponse', 100);
bindVisualRange('orb-bass-pulse', 'orbBassPulse', 100);
bindVisualRange('orb-topology', 'orbTopology', 100);
bindVisualRange('orb-fragmentation', 'orbFragmentation', 100);

const hueName = (degrees: number): string => {
  const names = ['Red', 'Orange', 'Amber', 'Yellow', 'Lime', 'Green', 'Aqua', 'Cyan', 'Sky', 'Blue', 'Indigo', 'Violet', 'Magenta', 'Pink', 'Rose', 'Red'];
  const index = Math.round(((degrees % 360) / 360) * (names.length - 1));
  return names[index];
};

const setBandHueValue = (input: HTMLInputElement, bandIndex: number, degrees: number, commit = true): void => {
  const displayDegrees = Math.round(degrees);
  if (input.value !== displayDegrees.toString()) input.value = displayDegrees.toString();
  visualSettings.bandHues[bandIndex] = degrees / 360;
  if (commit) visualizer.setSettings({ bandHues: [...visualSettings.bandHues] });
  input.closest<HTMLElement>('.band-color-row')?.style.setProperty('--band-hue', degrees.toString());
  input.nextElementSibling!.textContent = `${displayDegrees}°`;
  input.setAttribute('aria-valuetext', `${hueName(degrees)}, ${displayDegrees} degrees`);
};

const bindBandHue = (input: HTMLInputElement, bandIndex: number): void => {
  const update = (): void => {
    const degrees = Number(input.value);
    setBandHueValue(input, bandIndex, degrees);
    markColorThemeCustom();
    updateColorThemePreview();
  };
  input.addEventListener('input', update);
  setBandHueValue(input, bandIndex, Number(input.value));
};

DEFAULT_BAND_HUES.forEach((_, bandIndex) => {
  bindBandHue(query<HTMLInputElement>(`#band-hue-${bandIndex}`), bandIndex);
});
updateColorThemePreview();

colorThemeSelect.addEventListener('change', () => {
  const theme = COLOR_THEMES[colorThemeSelect.value as ColorThemeId];
  if (!theme) return;
  clearColorAutoAdjust();
  theme.forEach((degrees, bandIndex) => {
    setBandHueValue(query<HTMLInputElement>(`#band-hue-${bandIndex}`), bandIndex, degrees, false);
  });
  visualizer.setSettings({ bandHues: [...visualSettings.bandHues] });
  updateColorThemePreview();
  showMessage(`${colorThemeSelect.options[colorThemeSelect.selectedIndex].text} particle theme applied`);
});

query<HTMLButtonElement>('#reset-controls').addEventListener('click', () => {
  clearAutoAdjust();
  clearOrbAutoAdjust();
  clearColorAutoAdjust();
  setGyrationEnabled(false);
  const defaults: Record<string, string> = {
    intensity: '94', distortion: '38', travel: '62',
    'orb-size': '100', 'orb-morph': '38', 'orb-response': '100',
    'orb-bass-pulse': '100',
    'orb-topology': '0', 'orb-fragmentation': '0',
  };
  DEFAULT_BAND_HUES.forEach((hue, bandIndex) => { defaults[`band-hue-${bandIndex}`] = hue.toString(); });
  for (const [id, value] of Object.entries(defaults)) {
    const input = query<HTMLInputElement>(`#${id}`);
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
  colorThemeSelect.value = 'horizon';
  updateColorThemePreview();
  visualizer.resetAttitude();
  showMessage('Particle, orb, color, motion, and Auto-Adjust settings reset');
});

document.querySelectorAll<HTMLButtonElement>('[data-open-sheet]').forEach((button) => {
  button.addEventListener('click', () => openSheet(button.dataset.openSheet!, button));
});
document.querySelectorAll<HTMLButtonElement>('[data-close-sheet]').forEach((button) => button.addEventListener('click', closeSheets));
query<HTMLElement>('#sheet-scrim').addEventListener('click', closeSheets);
window.addEventListener('resize', syncSheetSemantics);

engine.addEventListener('trackchange', () => {
  updateTrackUI();
  renderTrackState();
});
engine.addEventListener('statechange', (event) => {
  const state = (event as CustomEvent<string>).detail;
  if (state === 'playing' && previewMotionActive) setPreviewMotion(false);
  visualizer.setPlaying(state === 'playing');
  syncAutoAdjustUI();
  playButton.classList.toggle('is-playing', state === 'playing');
  playButton.classList.toggle('is-loading', state === 'loading');
  playButton.setAttribute('aria-label', engine.hasTrack
    ? `${state === 'playing' ? 'Pause' : 'Play'} ${engine.track.title}`
    : 'Choose a track or upload audio before playback');
  if (!previewMotionActive) liveState.textContent = state === 'playing' ? 'ANALYZING' : state.toUpperCase();
  liveState.style.color = state === 'error' ? 'var(--ember)' : 'var(--cyan)';
  if (state === 'ended') showMessage('Orbit complete. Replay or choose another track.');
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeSheets();
  if (event.code === 'Space' && !(event.target as HTMLElement).matches('button, input, select, textarea')) {
    event.preventDefault();
    const startOrToggle = engine.state === 'playing' ? engine.toggle() : tryStartPlayback();
    void startOrToggle.catch((error: unknown) => showMessage(error instanceof Error ? error.message : 'Audio could not start.', true));
  }
});

const siteLoader = query<HTMLElement>('#site-loader');
const loaderParticles = query<HTMLElement>('#site-loader-particles');
const loaderStatus = query<HTMLElement>('#site-loader-status');
const loaderProgress = query<HTMLElement>('#site-loader-progress');
const instrument = query<HTMLElement>('#instrument');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const loaderStartedAt = performance.now();

for (let index = 0; index < 28; index += 1) {
  const particle = document.createElement('i');
  particle.style.setProperty('--particle-index', index.toString());
  particle.style.setProperty('--particle-angle', `${index * (360 / 28) + ((index * 17) % 13)}deg`);
  const particleDistance = 28 + ((index * 19) % 28);
  particle.style.setProperty('--particle-distance', `${particleDistance}px`);
  particle.style.setProperty('--particle-mid', `${particleDistance * 0.62}px`);
  particle.style.setProperty('--particle-inner', `${particleDistance * 0.34}px`);
  particle.style.setProperty('--particle-size', `${index % 6 === 0 ? 2.4 : 1.25 + (index % 4) * 0.22}px`);
  loaderParticles.append(particle);
}

const runEntrance = (): void => {
  const entrance = gsap.timeline({ defaults: { ease: 'expo.out' } });
  entrance
    .fromTo('.hud-rail--left', { clipPath: 'inset(0 0 96% 0)', opacity: 0.35 }, { clipPath: 'inset(0 0 0% 0)', opacity: 1, duration: 1.05 }, 0.05)
    .fromTo('.hud-rail--right .control-module, .reset-control', { x: 18, opacity: 0.2 }, { x: 0, opacity: 1, duration: 0.8, stagger: 0.07 }, 0.18)
    .fromTo('.transport', { y: 18, opacity: 0.2, backdropFilter: 'blur(0px)' }, { y: 0, opacity: 1, backdropFilter: 'blur(12px)', duration: 0.75 }, 0.32);
};

const dismissSiteLoader = (): void => {
  loaderStatus.textContent = 'Horizon acquired';
  loaderProgress.style.transform = 'scaleX(1)';
  siteLoader.classList.add('is-complete');
  window.setTimeout(() => {
    siteLoader.classList.add('is-exiting');
    instrument.removeAttribute('inert');
    runEntrance();
    window.setTimeout(() => siteLoader.remove(), reducedMotion ? 20 : 720);
  }, reducedMotion ? 20 : 340);
};

const loaderMinimum = reducedMotion ? 80 : 1650;
const completeAfterMinimum = (): void => {
  const remaining = Math.max(0, loaderMinimum - (performance.now() - loaderStartedAt));
  window.setTimeout(dismissSiteLoader, remaining);
};

const startMobileExperience = async (): Promise<void> => {
  if (mobileExperienceStarted || !window.matchMedia('(max-width: 900px)').matches) return;
  mobileExperienceStarted = true;
  enableMobileStartupAutoAdjust();
  const started = await loadCuratedTrack(MOBILE_STARTUP_TRACK, false);
  if (started) showMessage(MOBILE_STARTUP_TRACK.title + ' is now in orbit. Auto-Adjust flowing.', false, true);
  if (!started && previewMotionActive) {
    window.setTimeout(() => showMessage('Visual preview active. Tap Play to enable audio.', false, true), reducedMotion ? 120 : 1950);
  }
};

window.addEventListener('resize', () => {
  if (window.matchMedia('(max-width: 900px)').matches && !mobileExperienceStarted) void startMobileExperience();
});

// Stage the mobile track while the event-horizon loader is still visible.
void startMobileExperience();

if (document.readyState === 'complete') {
  completeAfterMinimum();
} else {
  window.addEventListener('load', completeAfterMinimum, { once: true });
}

updateTrackUI();
syncSheetSemantics();
syncAutoAdjustUI();
syncOrbAutoAdjustUI();
syncColorAutoAdjustUI();
requestAnimationFrame(frame);
if (!window.matchMedia('(max-width: 900px)').matches) {
  setTimeout(() => showMessage('Hover the visual field to preview the orbital response.', false, true), loaderMinimum + 650);
}
